import { useState, useRef } from 'react';
import { LayoutMode, GroupByOption, SortByOption } from '../views/dashboard/types';

const SESSION_STORAGE_KEY = 'dashboardSettings';

// Helper: columns that should default to descending
const NUMERIC_SORT_COLUMNS = [
    'totalTime', 'executions', 'avgTime', 'alerts', 'changes', 'custom', 'cpu', 'disk', 'mem'
];

const DEFAULT_FILTERS: Record<string, string[]> = {
    sensorstatus: [],
    type: [],
    host: [],
    tag: [],
    hostType: [],
    agent: []
};

const useDashboardState = () => {
    const initialLoadDone = useRef(false);

    // Helper to get initial state from session storage or defaults
    const getInitialState = <T,>(key: string, defaultValue: T): T => {
        if (initialLoadDone.current) {
            return defaultValue;
        }
        try {
            const storedValue = sessionStorage.getItem(SESSION_STORAGE_KEY);
            if (storedValue) {
                const settings = JSON.parse(storedValue);
                return settings[key] !== undefined ? settings[key] : defaultValue;
            }
        } catch (error) {
            console.error("Error reading estate view settings from session storage:", error);
            sessionStorage.removeItem(SESSION_STORAGE_KEY);
        }
        return defaultValue;
    };

    // Initialize filters - merge stored filters with defaults to ensure all keys exist
    const [filters, setFilters] = useState<Record<string, string[]>>(() => {
        const storedFilters = getInitialState('filters', DEFAULT_FILTERS);
        // Merge with defaults to ensure new filter keys are always present
        return { ...DEFAULT_FILTERS, ...storedFilters };
    });

    const [searchTerm, setSearchTerm] = useState<string>(() => getInitialState('searchTerm', ''));
    const [layoutMode, setLayoutMode] = useState<LayoutMode>(() => getInitialState('layoutMode', 'comfortable'));
    const [groupBy, setGroupBy] = useState<GroupByOption>(() => getInitialState('groupBy', 'none'));
    const [selectedTagNameForGrouping, setSelectedTagNameForGrouping] = useState<string | null>(() => getInitialState('selectedTagNameForGrouping', null));
    const [sortBy, setSortBy] = useState<SortByOption>(() => getInitialState('sortBy', 'name'));
    const [selectedTagNameForSorting, setSelectedTagNameForSorting] = useState<string | null>(() => getInitialState('selectedTagNameForSorting', null));
    const [sortAsc, setSortAsc] = useState<boolean>(() => {
        const initialSortBy = getInitialState('sortBy', 'name');
        return !NUMERIC_SORT_COLUMNS.includes(initialSortBy);
    });

    // Helper to update a specific filter group
    const updateFilter = (filterName: string, values: string[]) => {
        setFilters(prev => ({
            ...prev,
            [filterName]: values
        }));
    };

    // Clear all filters helper
    const clearAllFilters = () => {
        setFilters({ ...DEFAULT_FILTERS });
    };

    // Save state to session storage
    const saveState = () => {
        if (initialLoadDone.current) {
            const settingsToSave = {
                searchTerm,
                filters,
                layoutMode,
                groupBy,
                selectedTagNameForGrouping,
                sortBy,
                selectedTagNameForSorting,
                sortAsc,
            };
            try {
                sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(settingsToSave));
            } catch (error) {
                console.error("Error saving estate view settings to session storage:", error);
            }
        }
    };

    // Mark initial load as done
    if (!initialLoadDone.current) {
        initialLoadDone.current = true;
    }

    return {
        searchTerm,
        setSearchTerm,
        filters,
        updateFilter,
        clearAllFilters,
        layoutMode,
        setLayoutMode,
        groupBy,
        setGroupBy,
        selectedTagNameForGrouping,
        setSelectedTagNameForGrouping,
        sortBy,
        setSortBy,
        selectedTagNameForSorting,
        setSelectedTagNameForSorting,
        sortAsc,
        setSortAsc,
        saveState,
        NUMERIC_SORT_COLUMNS
    };
};

export default useDashboardState; 